import React, { memo } from 'react'
import { Link } from 'react-router-dom'
import { AppointmentTag } from 'transfix-ui/components/AppointmentTag'
import { MapMarker } from 'transfix-ui/components/MapMarker'
import { TableRow, TableCell } from 'transfix-ui/components/Table'
import { Box, Typography } from 'transfix-ui/core'
import { IShipperRateTypes } from 'types/graphqlTypes'
import {
  ShipmentIcons,
  getIcons,
} from 'apps/marketplace/pages/shipment-details/components/ShipmentIcons'
import { useShipmentIdContext } from 'apps/marketplace/pages/shipment-details/context/ShipmentIdContext'
import { DropTrailerIcon } from 'transfix-icons'
import { formatDate, getTimeAgoShort } from 'utils/formatDatetime'
import { DISPLAY } from 'constants/display'
import { TOnHoldStops } from '../types'

interface IPendingAppointmentsTableRowProps {
  index: number
  onHoldStop: TOnHoldStops[number]
}

export const PendingAppointmentsTableRow = memo<IPendingAppointmentsTableRowProps>(
  ({ index, onHoldStop }) => {
    const {
      appointment,
      autoAppointmentSchedule,
      city,
      dropTrailer,
      shipment,
      stateCode,
      stopSequence,
      stopType,
    } = onHoldStop
    const currentShipmentId = useShipmentIdContext()
    const { characteristics, id: shipmentId, shipperApplication, shipperRateType } = shipment || {}
    const { endAt, startAt, status, timeUpdatedAt, timeUpdatedBy, timezone } = appointment || {}
    const { lastRequestedAt } = autoAppointmentSchedule || {}
    const { name = '' } = shipperApplication || {}

    const appointmentDate = formatDate(startAt, 'dayFormat')
    const isSameShipment = currentShipmentId === shipmentId
    const stopLocation = `${city}, ${stateCode}`
    const lasEdited = `${getTimeAgoShort(timeUpdatedAt || '')} by ${timeUpdatedBy?.fullName}`
    const lastAutoRequest = lastRequestedAt ? getTimeAgoShort(lastRequestedAt) : DISPLAY.empty
    const icons = getIcons({
      ...characteristics,
      pricedUnderContract: Boolean(
        shipperRateType && shipperRateType === IShipperRateTypes.contract_primary
      ),
      contractBackup: Boolean(
        shipperRateType && shipperRateType === IShipperRateTypes.contract_backup
      ),
    })
    const shipperAbbreviation = name.substring(0, 3).toUpperCase()

    return (
      <TableRow dataTest='pending-appointments-table-row'>
        <TableCell>{index}</TableCell>
        <TableCell>
          <Box>
            <Box pr={1} component='span'>
              <Typography variant='body1' component='span'>
                {shipperAbbreviation}
              </Typography>
            </Box>
            <Link
              to={`/shipment-details/${shipmentId}?tab=scheduling`}
              target='_blank'
              rel='noopener noreferrer'
              data-test='shipment-link'>
              <Box display='flex' alignItems='center'>
                <Typography variant='body1' component='span'>
                  {shipmentId}
                </Typography>
                <ShipmentIcons icons={icons} />
              </Box>
            </Link>
            {isSameShipment && (
              <Typography variant='body2' color='textSecondary' component='p'>
                This Shipment
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell>
          <MapMarker sequence={stopSequence} type={stopType} markerStyle='newWave' />
        </TableCell>
        <TableCell>
          <Box>
            <Box display='flex' alignItems='center'>
              <Box mr={1}>
                <Typography variant='body1' component='span'>
                  {appointmentDate}
                </Typography>
              </Box>
              <AppointmentTag
                start={{
                  datetime: startAt,
                  timezone,
                }}
                end={{
                  datetime: endAt,
                  timezone,
                }}
                status={status || 'unconfirmed'}
              />
              {dropTrailer && (
                <Box display='flex' ml={0.5}>
                  <DropTrailerIcon color='coolGray5' size='large' />
                </Box>
              )}
            </Box>
            <Typography variant='body2' color='textSecondary' component='span'>
              {stopLocation}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant='body1' component='span'>
            {lasEdited}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant='body1' component='span'>
            {lastAutoRequest}
          </Typography>
        </TableCell>
      </TableRow>
    )
  }
)
